import type { IComponentController } from 'angular';
import template from './dashboard-configuration-panel.component.html';
import type { WidgetInstance } from '../dashboard.service';
import { DashboardPageComponent, DashboardPageController } from '../dashboard-page/dashboard-page.component';
import type { I18n } from '../../i18n/mx-internationalization';
import { ActionContextType, ActionsContext } from '../../Shell/Pandora/model/action';
import type { MxActionManagerService } from '../../Shell/Pandora/services/mx-action-manager-service';
import type { NotificationService } from '../../App/services/mx-notification-service';
import type { SidePanelService } from '../../Shell/Pandora/services/mx-side-panel-service';
import type { WizardService } from '../../Shell/PandoraSolutionBuilderAgent/services/wizard-service';
import { WizardMode } from '../../Shell/PandoraSolutionBuilderAgent/model/enumerations';
import { EventCodes } from '../../App/model/event-codes';
import type { EventService } from '../../App/services/event-service';

type WidgetInstanceAction = {
	icon: string;
	label: string;
	hidden?: boolean;
	onItemClick: () => void;
};

export class DashboardConfigurationPanelController implements IComponentController {
	page!: DashboardPageController;
	actions = new Map<string, WidgetInstanceAction[]>();

	static $inject = [
		'mx.internationalization',
		'mx.shell.ActionManagerService',
		'mx.shell.NotificationService',
		'mx.shell.SidePanelService',
		'mx.SolutionBuilderAgent.WizardService',
		'mx.shell.EventService',
	];
	constructor(
		private readonly i18n: I18n,
		private readonly actionManagerService: MxActionManagerService,
		private readonly notificationService: NotificationService,
		private readonly sidePanelService: ReturnType<typeof SidePanelService>,
		private readonly wizardService: ReturnType<typeof WizardService>,
		private readonly eventService: EventService
	) {}

	onShow(widgetInstance: WidgetInstance) {
		this.page.showWidgetInstance(widgetInstance);
	}

	onEdit(widgetInstance: WidgetInstance) {
		this.page.editWidget(widgetInstance);
		this.page.trackWidgetInstanceTelemetry(`Widget Configure clicked`, widgetInstance, {
			sender: 'panel',
		});
	}

	onHide(widgetInstance: WidgetInstance) {
		this.page.hideWidgetInstance(widgetInstance);
		this.page.trackWidgetInstanceTelemetry(`Widget made hidden`, widgetInstance, {
			sender: 'panel',
		});
	}

	onMouseOn(widgetInstance: WidgetInstance) {
		this.page.highlightWidgetInstance(widgetInstance);
	}

	onMouseLeave() {
		this.page.hideHighlight();
	}

	onAddNewWidgetClick() {
		this.page.addEmptyWidgetInstance();
	}

	private async getOriginalShareActions() {
		const actionContext = new ActionsContext(['PDRWidgetInstanceType'], []);

		const selectedObjects = [
			{
				_id: 'PDRWidgetInstanceType',
				_type: 'PDRWidgetInstanceType',
				_displayName: 'PDRWidgetInstanceType',
			},
		];
		const actions = this.actionManagerService.filterActions(
			await this.actionManagerService.getActions(actionContext, [ActionContextType.Single], selectedObjects),
			undefined,
			selectedObjects
		);
		return actions.find((action) => action.name.startsWith('UUX.Client.WidgetInstance.Share'));
	}

	getActions(widgetInstance: WidgetInstance): WidgetInstanceAction[] {
		if (this.actions.has(widgetInstance.id)) {
			return this.actions.get(widgetInstance.id) as WidgetInstanceAction[];
		}
		const actions: WidgetInstanceAction[] = [];

		const copyAction: WidgetInstanceAction = {
			icon: 'content_copy',
			label: this.i18n.get('solutionBuilderAgent.common.copy'),
			onItemClick: async () => {
				await this.page.copyWidgetInstance(widgetInstance);
			},
		};

		const shareAction: WidgetInstanceAction = {
			icon: 'share',
			label: this.i18n.get('solutionBuilderAgent.common.share'),
			hidden: !widgetInstance.isEditable,
			onItemClick: () => {
				const wizardView = this.wizardService.getWizardView({
					widgetId: 'a54d3c0e-204f-ef11-0486-ec63d7895110',
					wizardMode: WizardMode.Default,
					name: this.i18n.get('solutionBuilderAgent.common.share'),
					displayName: this.i18n.get('solutionBuilderAgent.common.share'),
					selectedObjects: [
						{
							_id: widgetInstance.id,
							_type: 'PDRWidgetInstanceType',
							_displayName: widgetInstance.title,
						},
					],
				});

				// @ts-expect-error no types
				wizardView.id = Date.now();
				this.sidePanelService.addView(wizardView);

				this.eventService.dispatchEvent(EventCodes.TrackTelemetry, {
					eventName: `Action clicked`,
					sender: 'cab47ac4-33f1-c984-428b-08dca7f6388d',
					senderTitle: 'UUX.Widget.Share',

					contextId: [widgetInstance.id],
					contextTitle: [widgetInstance.title],
					contextTypeId: ['5e1a3dee-f43d-ef11-47be-c403a89c6909'],
					contextTypeTitle: ['PDRWidgetInstanceType'],
				});
			},
		};

		if (shareAction.hidden) {
			this.getOriginalShareActions().then((originalShare) => {
				if (originalShare) {
					shareAction.hidden = false;
				}
			});
		}

		const removeAction: WidgetInstanceAction = {
			icon: 'delete',
			label: this.i18n.get('solutionBuilderAgent.common.remove'),
			onItemClick: async () => {
				try {
					await this.notificationService.confirm(this.i18n.get('shell.dashboards.removeConfirmationTitle'), this.i18n.get('shell.dashboards.removeConfirmationPrompt'));
					await this.page.removeWidgetInstance(widgetInstance);
					this.notificationService.success(this.i18n.get('shell.dashboards.removeNotification'));
					return true;
				} catch (e) {
					return false;
				}
			},
		};

		actions.push(copyAction);
		actions.push(shareAction);
		if (widgetInstance.isEditable) {
			actions.push(removeAction);
		}

		this.actions.set(widgetInstance.id, actions);

		return actions;
	}
}

export const DashboardConfigurationPanelComponent = {
	name: 'mxDashboardConfigurationPanel',
	controller: DashboardConfigurationPanelController,
	controllerAs: 'vm',
	template,
	require: {
		page: `^${DashboardPageComponent.name}`,
	},
};
